/**
 * useWhatsAppMessaging Hook
 * Manages WhatsApp messaging logic, template operations, and AUTO-FILL functionality
 */

import { useState, useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import {
  WhatsAppMessageTemplate,
  Customer,
  Promotion,
} from "../types/loyalty.types";
import {
  formatMessage,
  generateWhatsAppLink,
  extractVariablesFromTemplate,
  isValidPhoneNumber,
  generateBulkWhatsAppLinks,
  createAutoFilledVariables,
} from "../utils/whatsappHelper";
import {
  DEFAULT_WHATSAPP_TEMPLATES,
  getAllWhatsAppTemplates,
} from "../constants/whatsappTemplates";
import { getMyRestaurant } from "../api/restaurant.api";
import type { RestaurantDetails } from "../api/restaurant.api";
import { promotionsApi } from "../api/backend-loyalty.api";

const QUERY_KEYS = {
  templates: (restaurantId: number) => ["whatsapp-templates", restaurantId],
  messages: (restaurantId: number) => ["whatsapp-messages", restaurantId],
};

export function useWhatsAppMessaging(restaurantId?: number) {
  const queryClient = useQueryClient();
  const [restaurantIdState] = useState(restaurantId);
  const [restaurantData, setRestaurantData] =
    useState<RestaurantDetails | null>(null);
  const [restaurantLoading, setRestaurantLoading] = useState(false);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("authToken") || undefined
      : undefined;

  // Resolve restaurantId
  let resolvedRestaurantId = restaurantIdState;
  if (!resolvedRestaurantId && token) {
    try {
      const decoded: any = jwtDecode(token);
      if (decoded?.restaurantId) {
        resolvedRestaurantId = parseInt(decoded.restaurantId, 10);
      }
    } catch (err) {
      console.warn("Failed to decode token for restaurantId:", err);
    }
  }

  if (!resolvedRestaurantId) {
    const storedId =
      localStorage.getItem("businessId") ||
      localStorage.getItem("restaurantId");
    resolvedRestaurantId = storedId ? parseInt(storedId, 10) : 0;
  }

  /**
   * 🔥 NEW: Fetch restaurant data on component mount
   * This data is used for auto-filling restaurantName, restaurantPhone, etc.
   */
  useEffect(() => {
    const fetchRestaurantData = async () => {
      if (!resolvedRestaurantId) return;

      try {
        setRestaurantLoading(true);
        // Use getMyRestaurant() to get the logged-in restaurant's data
        const data = await getMyRestaurant();
        setRestaurantData(data);
      } catch (error) {
        console.error("Failed to load restaurant data for auto-fill:", error);
        // Set a fallback restaurant object so auto-fill doesn't completely fail
        setRestaurantData({
          id: resolvedRestaurantId,
          name: "Our Restaurant",
          isActive: true,
        });
      } finally {
        setRestaurantLoading(false);
      }
    };

    fetchRestaurantData();
  }, [resolvedRestaurantId]);

  const getSmartVariableExample = (varIndex: string, templateContent: string): string => {
    // A simple heuristic to guess what the variable label might be based on context
    // e.g. "Hi {{1}}, welcome..." -> {{1}} is likely Name
    
    // Safety check for content
    if (!templateContent) return `Variable ${varIndex}`;

    const parts = templateContent.split(`{{${varIndex}}}`);
    if (parts.length < 1) return `Variable ${varIndex}`;
    
    const nearbyText = parts[0].slice(-20); // 20 chars before
    
    if (/name|hi|hello|dear/i.test(nearbyText)) return "Customer Name";
    if (/points|balance/i.test(nearbyText)) return "Reward Points";
    if (/link|url|click/i.test(nearbyText)) return "Link / URL";
    if (/code|coupon/i.test(nearbyText)) return "Coupon Code";
    if (/restaurant|store|business/i.test(nearbyText)) return "Restaurant Name";
    
    return `Variable ${varIndex}`;
  };

  /**
   * Auto-fill template variables with customer and restaurant data
   */
  const autoFillTemplateVariables = useCallback(
    (
      content: string,
      customer: Partial<Customer>,
      customVariables?: Record<string, string>,
      promotion?: Promotion
    ): string => {
      if (!content) return "";

      const restaurant: RestaurantDetails = restaurantData ?? {
        id: 0,
        name: "Our Restaurant",
        isActive: true,
      };

      // Build the complete variable map using the shared helper (covers ALL template variables:
      // customerName, points, tier, benefits, expiryPoints, daysLeft, restaurantName,
      // discount, promoCode, offerDetails, expiryDate, minOrderValue, etc.)
      const autoVars = createAutoFilledVariables(
        customer as Customer,
        restaurant,
        promotion,
        (customer as any).loyaltyTier
      );

      // Merge custom overrides on top
      const merged: Record<string, string | number | undefined> = {
        ...autoVars,
        ...(customVariables ?? {}),
      };

      // Replace both {varName} and {{varName}} patterns
      let message = content;
      Object.entries(merged).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const val = String(value);
          message = message.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), val);
          message = message.replace(new RegExp(`\\{${key}\\}`, "g"), val);
        }
      });

      return message;
    },
    [restaurantData]
  );


  /**
   * Generate WhatsApp link for a customer
   */
  const generateCustomerWhatsAppLink = useCallback(
    (phoneNumber: string, message: string): string => {
      try {
        return generateWhatsAppLink(phoneNumber, message);
      } catch (error) {
        console.error("Failed to generate WhatsApp link:", error);
        throw error;
      }
    },
    []
  );

  /**
   * Validate customer phone number
   */
  const validateCustomerPhone = useCallback((phone?: string): boolean => {
    if (!phone) return false;
    return isValidPhoneNumber(phone);
  }, []);

  /**
   * Format a message with customer variables (Legacy Helper)
   */
  const formatCustomerMessage = useCallback(
    (
      templateContent: string,
      customer: Partial<Customer> & { [key: string]: any }
    ): string => {
      const variables: Record<string, string | number> = {
        customerName: customer.name || "Valued Customer",
        phone: customer.phone || "",
        points: customer.points || 0,
        tier: customer.loyaltyTier?.name || "Member",
        totalVisits: customer.totalVisits || 0,
        totalSpent: customer.totalSpent || 0,
      };

      return formatMessage(templateContent, variables);
    },
    []
  );

  /**
   * Get all templates from localStorage with DEFAULT_TEMPLATES fallback
   */
  const templatesQuery = useQuery({
    queryKey: QUERY_KEYS.templates(resolvedRestaurantId || 0),
    queryFn: async () => {
      // 1. Try fetching from Backend API first
      if (token && resolvedRestaurantId) {
        try {
          const apiTemplates = await promotionsApi.getTemplates(token, resolvedRestaurantId);
          if (Array.isArray(apiTemplates) && apiTemplates.length > 0) {
            return apiTemplates;
          }
        } catch (apiError) {
          console.warn("⚠️ Failed to fetch templates from API, falling back to local storage", apiError);
        }
      }

      // 2. Fallback to LocalStorage / Defaults
      try {
        const result = getAllWhatsAppTemplates();
        return result;
      } catch (error) {
        console.error("Failed to load templates, using defaults:", error);
        // Final fallback - return defaults directly
        return DEFAULT_WHATSAPP_TEMPLATES;
      }
    },
    enabled: true, // Always enabled so we at least get local templates
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  /**
   * Get a specific template by ID
   */
  const getTemplateById = useCallback(
    (templateId: string): WhatsAppMessageTemplate | null => {
      const templates = templatesQuery.data || [];
      return templates.find((t) => t.id === templateId) || null;
    },
    [templatesQuery.data]
  );

  /**
   * Get active templates only
   */
  const getActiveTemplates = useCallback(() => {
    const templates = templatesQuery.data || [];
    return templates.filter((t) => t.isActive);
  }, [templatesQuery.data]);

  /**
   * Get templates by type
   */
  const getTemplatesByType = useCallback(
    (type: string): WhatsAppMessageTemplate[] => {
      const templates = templatesQuery.data || [];
      return templates.filter((t) => t.type === type && t.isActive);
    },
    [templatesQuery.data]
  );

  /**
   * Get templates by category
   */
  const getTemplatesByCategory = useCallback(
    (category: string): WhatsAppMessageTemplate[] => {
      const templates = templatesQuery.data || [];
      return templates.filter((t) => t.category === category && t.isActive);
    },
    [templatesQuery.data]
  );

  /**
   * Create new template (save to localStorage)
   */
  const createTemplateMutation = useMutation({
    mutationFn: async (
      template: Omit<WhatsAppMessageTemplate, "id" | "createdAt">
    ) => {
      const variables = extractVariablesFromTemplate(template.content);
      const newTemplate: WhatsAppMessageTemplate = {
        ...template,
        id: `custom_${Date.now()}`,
        variables,
        createdAt: new Date().toISOString(),
      };

      const saved = localStorage.getItem("whatsapp_templates");
      const templates = saved ? JSON.parse(saved) : [];
      templates.push(newTemplate);
      localStorage.setItem("whatsapp_templates", JSON.stringify(templates));

      return newTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.templates(resolvedRestaurantId || 0),
      });
    },
  });

  /**
   * Update existing template
   */
  const updateTemplateMutation = useMutation({
    mutationFn: async ({
      id,
      template,
    }: {
      id: string;
      template: Partial<WhatsAppMessageTemplate>;
    }) => {
      const saved = localStorage.getItem("whatsapp_templates");
      let templates = saved ? JSON.parse(saved) : [];

      const variables = template.content
        ? extractVariablesFromTemplate(template.content)
        : undefined;

      templates = templates.map((t: WhatsAppMessageTemplate) =>
        t.id === id
          ? {
              ...t,
              ...template,
              variables: variables || t.variables,
              updatedAt: new Date().toISOString(),
            }
          : t
      );

      localStorage.setItem("whatsapp_templates", JSON.stringify(templates));
      return templates.find((t: WhatsAppMessageTemplate) => t.id === id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.templates(resolvedRestaurantId || 0),
      });
    },
  });

  /**
   * Delete template
   */
  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      if (id.startsWith("template_")) {
        throw new Error("Cannot delete default templates");
      }

      const saved = localStorage.getItem("whatsapp_templates");
      let templates = saved ? JSON.parse(saved) : [];
      templates = templates.filter((t: WhatsAppMessageTemplate) => t.id !== id);
      localStorage.setItem("whatsapp_templates", JSON.stringify(templates));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.templates(resolvedRestaurantId || 0),
      });
    },
  });

  /**
   * Generate WhatsApp links for bulk sending
   */
  const generateBulkLinks = useCallback(
    (
      customers: Array<{ id: string; name: string; phone: string }>,
      message: string
    ) => {
      return generateBulkWhatsAppLinks(customers, message);
    },
    []
  );

  /**
   * Prepare message for a customer (format + validate)
   */
  const prepareCustomerMessage = useCallback(
    (
      template: WhatsAppMessageTemplate,
      customer: Partial<Customer> & { [key: string]: any }
    ): { success: boolean; message?: string; error?: string } => {
      try {
        if (!validateCustomerPhone(customer.phone)) {
          return { success: false, error: "Invalid or missing phone number" };
        }

        const formattedMessage = formatCustomerMessage(
          template.content,
          customer
        );
        return { success: true, message: formattedMessage };
      } catch (error) {
        return { success: false, error: (error as Error).message };
      }
    },
    [formatCustomerMessage, validateCustomerPhone]
  );
  
  // Removed duplicate autoFillTemplateVariables definition


  /**
   * Send WhatsApp message (open link with pre-filled message)
   */
  const sendWhatsAppMessage = useCallback(
    (phoneNumber: string, message: string): boolean => {
      try {
        if (!validateCustomerPhone(phoneNumber)) {
          throw new Error("Invalid phone number");
        }

        const link = generateCustomerWhatsAppLink(phoneNumber, message);
        window.open(link, "_blank");
        return true;
      } catch (error) {
        console.error("Failed to send WhatsApp message:", error);
        return false;
      }
    },
    [validateCustomerPhone, generateCustomerWhatsAppLink]
  );

  /**
   * Prepare bulk send (generate all links without opening)
   */
  const prepareBulkSend = useCallback(
    (
      customers: Array<Partial<Customer> & { [key: string]: any }>,
      template: WhatsAppMessageTemplate
    ): Array<{
      customerId: string;
      customerName: string;
      phone: string;
      link: string;
      message: string;
      isValid: boolean;
      error?: string;
    }> => {
      return customers
        .filter((c) => c.id && c.name && c.phone)
        .map((customer) => {
          const phoneValid = validateCustomerPhone(customer.phone);
          const messageResult = prepareCustomerMessage(template, customer);

          if (phoneValid && messageResult.success) {
            return {
              customerId: customer.id!,
              customerName: customer.name!,
              phone: customer.phone!,
              message: messageResult.message!,
              link: generateCustomerWhatsAppLink(
                customer.phone!,
                messageResult.message!
              ),
              isValid: true,
            };
          } else {
            return {
              customerId: customer.id!,
              customerName: customer.name!,
              phone: customer.phone!,
              message: "",
              link: "",
              isValid: false,
              error: messageResult.error || "Invalid customer data",
            };
          }
        });
    },
    [
      validateCustomerPhone,
      prepareCustomerMessage,
      generateCustomerWhatsAppLink,
    ]
  );

  /**
   * 🔥 NEW: Prepare bulk send WITH AUTO-FILL
   * This is the MAIN function to use for sending messages
   * Automatically fills ALL variables for each customer
   *
   * @param customers - Array of customers to send to
   * @param template - Message template to use
   * @param promotion - Optional promotion data
   * @returns Array of prepared messages with WhatsApp links ready to send
   */
  const prepareBulkSendWithAutoFill = useCallback(
    (
      customers: Customer[],
      template: WhatsAppMessageTemplate,
      promotion?: Promotion
    ): Array<{
      customerId: string;
      customerName: string;
      phone: string;
      link: string;
      message: string;
      isValid: boolean;
      error?: string;
    }> => {
      return customers
        .filter((c) => c && c.id && c.name && c.phone)
        .map((customer) => {
          const phoneValid = validateCustomerPhone(customer.phone);

          try {
            if (!phoneValid) {
              return {
                customerId: customer.id,
                customerName: customer.name,
                phone: customer.phone,
                message: "",
                link: "",
                isValid: false,
                error: "Invalid phone number",
              };
            }

            // 🔥 AUTO-FILL happens here
            const formattedMessage = autoFillTemplateVariables(
              template.content,
              customer,
              undefined, 
              promotion
            );

            const link = generateCustomerWhatsAppLink(
              customer.phone,
              formattedMessage
            );

            return {
              customerId: customer.id,
              customerName: customer.name,
              phone: customer.phone,
              message: formattedMessage,
              link,
              isValid: true,
            };
          } catch (error) {
            console.error(
              `Failed to prepare message for customer ${customer.id}:`,
              error
            );
            return {
              customerId: customer.id,
              customerName: customer.name,
              phone: customer.phone,
              message: "",
              link: "",
              isValid: false,
              error: "Failed to generate message",
            };
          }
        });
    },
    [
      autoFillTemplateVariables,
      validateCustomerPhone,
      generateCustomerWhatsAppLink,
    ]
  );

  /**
   * Log message sending (for future backend integration)
   */
  const logMessageSent = useCallback(
    async (
      customerId: string,
      customerName: string,
      customerPhone: string,
      templateId: string,
      message: string
    ) => {
      // This is a placeholder for future backend integration
      // For now, we'll just log to console
      const logEntry = {
        customerId,
        customerName,
        customerPhone,
        templateId,
        message:
          message.substring(0, 100) + (message.length > 100 ? "..." : ""),
        timestamp: new Date().toISOString(),
        restaurantId: resolvedRestaurantId,
      };


      // TODO: Send to backend when API is ready
      // await whatsappApi.logMessage(logEntry);
    },
    [resolvedRestaurantId]
  );

  return {
    // Queries
    templatesQuery,
    templates: (() => {
      const data = templatesQuery.data || DEFAULT_WHATSAPP_TEMPLATES;
      if (typeof window !== "undefined" && data.length > 0) {
      }
      return data;
    })(),

    // Restaurant data
    restaurantData,
    restaurantLoading,

    // Template operations
    getTemplateById,
    getActiveTemplates,
    getTemplatesByType,
    getTemplatesByCategory,
    createTemplateMutation,
    updateTemplateMutation,
    deleteTemplateMutation,

    // Message operations (old)
    formatCustomerMessage,
    generateCustomerWhatsAppLink,
    validateCustomerPhone,
    prepareCustomerMessage,
    sendWhatsAppMessage,
    prepareBulkSend,
    generateBulkLinks,
    logMessageSent,

    // 🔥 NEW: Auto-fill operations
    autoFillTemplateVariables,
    prepareBulkSendWithAutoFill,
    getSmartVariableExample,
  };
}
